/** Client-side audio analysis + metadata for delivery files. */

export interface AudioMeta {
  durationSec?: number;
  sampleRate?: number;
  channels?: number;
  sizeBytes?: number;
  bitrateKbps?: number;
  mimeType?: string;
  source: "probe" | "partial" | "none";
}

export interface PeakSet {
  peaks: Float32Array;
  rms: Float32Array;
}

export interface AudioAnalysis {
  meta: AudioMeta;
  peaks: PeakSet;
}

const metaCache = new Map<string, AudioMeta>();
const analysisCache = new Map<string, AudioAnalysis>();

function compress(v: number): number {
  return Math.pow(Math.min(1, Math.max(0, v)), 0.72);
}

function formatFromMime(mime?: string): string | undefined {
  if (!mime) return undefined;
  if (mime.includes("mpeg") || mime.includes("mp3")) return "MP3";
  if (mime.includes("wav")) return "WAV";
  if (mime.includes("flac")) return "FLAC";
  if (mime.includes("mp4") || mime.includes("m4a") || mime.includes("aac")) return "M4A";
  if (mime.includes("ogg")) return "OGG";
  return mime.split("/").pop()?.toUpperCase();
}

export function formatBytes(bytes?: number): string | undefined {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return undefined;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 2 : 1)} MB`;
}

export function formatDuration(seconds?: number): string | undefined {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return undefined;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatSampleRate(hz?: number): string | undefined {
  if (!hz || !Number.isFinite(hz) || hz <= 0) return undefined;
  if (hz >= 1000) {
    const k = hz / 1000;
    return `${Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1)} kHz`;
  }
  return `${Math.round(hz)} Hz`;
}

export function formatBitrate(kbps?: number): string | undefined {
  if (!kbps || !Number.isFinite(kbps) || kbps <= 0) return undefined;
  return `${Math.round(kbps)} kbps`;
}

export function formatChannels(channels?: number): string | undefined {
  if (!channels || channels <= 0) return undefined;
  if (channels === 1) return "Mono";
  if (channels === 2) return "Stereo";
  return `${channels} ch`;
}

/** Pull BPM/key hints from titles like "For The Night 101 BPM". */
export function parseTitleHints(title?: string): { bpm?: number; key?: string } {
  if (!title) return {};
  const bpmMatch = title.match(/(\d{2,3})\s*(?:bpm|BPM)\b/);
  const keyMatch = title.match(/\b([A-G](?:#|b)?)(?:\s*(major|minor|maj|min)|\b(m)\b)?\b/);
  let key: string | undefined;
  if (keyMatch) {
    const note = keyMatch[1];
    const quality = (keyMatch[2] || keyMatch[3] || "").toLowerCase();
    if (!quality) {
      // Avoid treating random single letters in titles as keys unless explicit
      key = undefined;
    } else if (quality.startsWith("maj")) key = `${note} major`;
    else if (quality === "m" || quality.startsWith("min")) key = `${note} minor`;
  }
  // Explicit patterns like "Key: Am" or "- Am -"
  const explicitKey =
    title.match(/\bkey[:\s]+([A-G](?:#|b)?\s*(?:major|minor|maj|min|m)?)\b/i)?.[1] ||
    title.match(/\s-\s([A-G](?:#|b)?m?)\s-\s/)?.[1];
  if (explicitKey) key = explicitKey.trim();

  return {
    bpm: bpmMatch ? Number(bpmMatch[1]) : undefined,
    key,
  };
}

function buildPeaks(audioBuffer: AudioBuffer, barCount: number): PeakSet {
  const channels = Math.min(2, audioBuffer.numberOfChannels);
  const left = audioBuffer.getChannelData(0);
  const right = channels > 1 ? audioBuffer.getChannelData(1) : left;
  const total = left.length;
  const block = Math.max(1, Math.floor(total / barCount));
  const peaks = new Float32Array(barCount);
  const rms = new Float32Array(barCount);

  for (let i = 0; i < barCount; i++) {
    const start = i * block;
    const end = Math.min(total, start + block);
    let peak = 0;
    let sumSq = 0;
    let count = 0;
    const step = Math.max(1, Math.floor((end - start) / 220));
    for (let j = start; j < end; j += step) {
      const l = left[j] ?? 0;
      const r = right[j] ?? 0;
      const abs = Math.max(Math.abs(l), Math.abs(r));
      if (abs > peak) peak = abs;
      const mid = (l + r) * 0.5;
      sumSq += mid * mid;
      count += 1;
    }
    peaks[i] = peak;
    rms[i] = count > 0 ? Math.sqrt(sumSq / count) : 0;
  }

  let peakMax = 0.0001;
  let rmsMax = 0.0001;
  for (let i = 0; i < barCount; i++) {
    if (peaks[i] > peakMax) peakMax = peaks[i];
    if (rms[i] > rmsMax) rmsMax = rms[i];
  }
  for (let i = 0; i < barCount; i++) {
    peaks[i] = Math.max(0.02, compress(peaks[i] / peakMax));
    rms[i] = Math.max(0.01, compress(rms[i] / rmsMax) * 0.85);
  }
  return { peaks, rms };
}

/** Decode once: waveform peaks + technical metadata. */
export async function analyzeAudio(
  url: string,
  barCount = 160,
): Promise<AudioAnalysis> {
  const cacheKey = `${url}::${barCount}`;
  const cached = analysisCache.get(cacheKey);
  if (cached) return cached;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Audio analyze failed (${res.status})`);
  const mimeType = res.headers.get("content-type") || undefined;
  const buffer = await res.arrayBuffer();
  const sizeBytes =
    Number(res.headers.get("content-length")) > 0
      ? Number(res.headers.get("content-length"))
      : buffer.byteLength;

  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  try {
    const audioBuffer = await ctx.decodeAudioData(buffer.slice(0));
    const durationSec = audioBuffer.duration;
    const sampleRate = audioBuffer.sampleRate;
    const channels = audioBuffer.numberOfChannels;
    const bitrateKbps =
      durationSec > 0 ? Math.round((sizeBytes * 8) / durationSec / 1000) : undefined;

    const meta: AudioMeta = {
      durationSec,
      sampleRate,
      channels,
      sizeBytes,
      bitrateKbps,
      mimeType,
      source: "probe",
    };
    metaCache.set(url, meta);

    const peaks = buildPeaks(audioBuffer, barCount);
    const analysis = { meta, peaks };
    analysisCache.set(cacheKey, analysis);
    return analysis;
  } finally {
    void ctx.close();
  }
}

export async function probeAudioMeta(url: string): Promise<AudioMeta> {
  const cached = metaCache.get(url);
  if (cached?.source === "probe") return cached;

  try {
    // Reuse full analysis path (also warms waveform cache for default bar count)
    const analysis = await analyzeAudio(url, 160);
    return analysis.meta;
  } catch {
    // Lightweight size/mime fallback
    try {
      const head = await fetch(url, { headers: { Range: "bytes=0-0" } });
      const mimeType = head.headers.get("content-type") || undefined;
      const contentRange = head.headers.get("content-range");
      const total = contentRange?.split("/")[1];
      const sizeBytes = total ? Number(total) : undefined;
      const meta: AudioMeta = {
        mimeType,
        sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : undefined,
        source: "partial",
      };
      metaCache.set(url, meta);
      return meta;
    } catch {
      const meta: AudioMeta = { source: "none" };
      metaCache.set(url, meta);
      return meta;
    }
  }
}

export function metaPills(input: {
  bpm?: number;
  key?: string;
  format?: string;
  meta?: AudioMeta | null;
}): { label: string; value: string }[] {
  const pills: { label: string; value: string }[] = [];
  const meta = input.meta;

  if (input.bpm) pills.push({ label: "BPM", value: String(input.bpm) });
  if (input.key) pills.push({ label: "Key", value: input.key });

  const duration = formatDuration(meta?.durationSec);
  if (duration) pills.push({ label: "Length", value: duration });

  const sr = formatSampleRate(meta?.sampleRate);
  if (sr) pills.push({ label: "Sample rate", value: sr });

  const br = formatBitrate(meta?.bitrateKbps);
  if (br) pills.push({ label: "Bitrate", value: br });

  const ch = formatChannels(meta?.channels);
  if (ch) pills.push({ label: "Channels", value: ch });

  const size = formatBytes(meta?.sizeBytes);
  if (size) pills.push({ label: "Size", value: size });

  const format = input.format || formatFromMime(meta?.mimeType);
  if (format) pills.push({ label: "Format", value: format });

  return pills;
}
